import React, { useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Alert,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People,
  AccountBalanceWallet,
  Person,
  ExitToApp,
  Add,
  Store,
  Casino,
  TrendingUp,
  Receipt,
  Assessment,
  Announcement,
  MoneyOff,
  Settings as SettingsIcon,
  NotificationsActive,
  HelpOutline,
  Send,
  Payment,
  AdminPanelSettings,
  FlightTakeoff,
  History,
  Lock,
  ViewCarousel as BannerIcon,
} from "@mui/icons-material";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { userAPI } from "../../services/api";

// Import dashboard components
import Overview from "./Overview";
import UserManagement from "./UserManagement";
import WalletManagement from "./WalletManagement";
import WithdrawalManagement from "./WithdrawalManagement";
import RechargeManagement from "./RechargeManagement";
import WithdrawalTest from "./WithdrawalTest";
import Profile from "./Profile";
import CreateUser from "./CreateUser";
import MarketManagement from "./MarketManagement";
import CreateMarket from "./CreateMarket";
import MarketDetails from "./MarketDetails";

// Import Gali components
import GaliMarkets from "./Gali/GaliMarkets";
import GaliGameRates from "./Gali/GaliGameRates";
import GaliBets from "./Gali/GaliBets";
import GaliDeclareResults from "./Gali/GaliDeclareResults";

// Import Matka components
import MatkaGameRates from "./Matka/MatkaGameRates";
import MatkaBets from "./Matka/MatkaBets";
import MatkaDeclareResults from "./Matka/MatkaDeclareResults";

// Import Settings components
import NoticeboardManagement from "./Settings/NoticeboardManagement";
import HowToPlayManagement from "./Settings/HowToPlayManagement";
import PushNotificationManagement from "./Settings/PushNotificationManagement";
import MerchantUPIManagement from "./Settings/MerchantUPIManagement";
import AdminSettingsManagement from "./Settings/AdminSettingsManagement";
import BannerManagement from "./Settings/BannerManagement";

// Import Aviator components
import AviatorSettings from "./Aviator/AviatorSettings";
import AviatorStatistics from "./Aviator/AviatorStatistics";
import AviatorBetHistory from "./Aviator/AviatorBetHistory";

// Import Chicken Road components
import ChickenRoadSettings from "./ChickenRoad/ChickenRoadSettings";
import ChickenRoadStatistics from "./ChickenRoad/ChickenRoadStatistics";
import ChickenRoadHistory from "./ChickenRoad/ChickenRoadHistory";

// Import 7 Up Down components
import SevenUpDownStatistics from "./SevenUpDown/SevenUpDownStatistics";
import SevenUpDownBetHistory from "./SevenUpDown/SevenUpDownBetHistory";
import SevenUpDownSettings from "./SevenUpDown/SevenUpDownSettings";

const drawerWidth = 240;

// Protected Route Component
const ProtectedRoute = ({ element, allowedRoles }) => {
  const { user } = useAuth();
  
  if (!allowedRoles.includes(user?.user_type)) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2">
            You do not have permission to access this page. Your role: <strong>{user?.user_type}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Required roles: <strong>{allowedRoles.join(", ")}</strong>
          </Typography>
        </Alert>
      </Container>
    );
  }
  
  return element;
};

const Dashboard = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
    setPasswordError("");
    setPasswordSuccess("");
    setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
    handleProfileMenuClose();
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordData({ ...passwordData, [field]: event.target.value });
    setPasswordError("");
  };

  const handlePasswordSubmit = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      setPasswordError("All fields are required");
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError("New password must be at least 6 characters long");
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordData.old_password === passwordData.new_password) {
      setPasswordError("New password must be different from old password");
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await userAPI.updatePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });

      if (response.data.success) {
        setPasswordSuccess("Password updated successfully!");
        setTimeout(() => {
          handlePasswordDialogClose();
        }, 1500);
      } else {
        setPasswordError(response.data.message || "Failed to update password");
      }
    } catch (error) {
      console.error("Password update error:", error);
      setPasswordError(
        error.response?.data?.message || "Failed to update password. Please try again."
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const menuItems = [
    {
      text: "Overview",
      icon: <DashboardIcon />,
      path: "/dashboard",
      roles: ["owner", "super_admin","admin", "agent" ],
    },
    {
      text: "User Management",
      icon: <People />,
      path: "/dashboard/users",
      roles: ["owner", "super_admin" , "admin", "agent"],
    },
    {
      text: "Create User",
      icon: <Add />,
      path: "/dashboard/create-user",
      roles: ["owner", "super_admin" , "admin", "agent"],
    },
    {
      text: "Wallet",
      icon: <AccountBalanceWallet />,
      path: "/dashboard/wallet",
      roles: ["owner", "super_admin" , "admin", "agent"],
    },
    {
      text: "Withdrawals",
      icon: <MoneyOff />,
      path: "/dashboard/withdrawals",
      roles: ["owner", ],
    },
    {
      text: "Recharges",
      icon: <AccountBalanceWallet />,
      path: "/dashboard/recharges",
      roles: ["owner",],
    },
    // {
    //   text: "Withdrawal Test",
    //   icon: <Assessment />,
    //   path: "/dashboard/withdrawal-test",
    //   roles: ["owner", "super_admin" , "admin" ],
    // },
    
    
    // Gali Management Section
    {
      text: "Gali Management",
      isHeader: true,
      roles: ["owner", "super_admin" , "admin" ],
    },
    {
      text: "Gali Markets",
      icon: <Casino />,
      path: "/dashboard/gali/markets",
      roles: ["owner", "super_admin" , "admin"  ],
      isSubmenu: true,
    },
    {
      text: "Gali Bets",
      icon: <Receipt />,
      path: "/dashboard/gali/bets",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Gali Game Rates",
      icon: <TrendingUp />,
      path: "/dashboard/gali/rates",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Gali Declare & Past Results",
      icon: <Announcement />,
      path: "/dashboard/gali/results",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    
    // Matka Management Section
    {
      text: "Matka Management",
      isHeader: true,
      roles: ["owner", "super_admin" , "admin" ],
    },
    {
      text: "Markets",
      icon: <Store />,
      path: "/dashboard/markets",
      roles: ["owner", "super_admin" , "admin" ],
            isSubmenu: true,

    },
    {
      text: "Matka Game Rates",
      icon: <TrendingUp />,
      path: "/dashboard/matka/rates",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Matka Bets",
      icon: <Receipt />,
      path: "/dashboard/matka/bets",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Matka Declare & Results",
      icon: <Announcement />,
      path: "/dashboard/matka/results",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    
    // Aviator Management Section
    {
      text: "Aviator Management",
      isHeader: true,
      roles: ["owner", "super_admin" , "admin" ],
    },
    {
      text: "Aviator Statistics",
      icon: <Assessment />,
      path: "/dashboard/aviator/statistics",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Bet History",
      icon: <Receipt />,
      path: "/dashboard/aviator/bets",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Aviator Settings",
      icon: <FlightTakeoff />,
      path: "/dashboard/aviator/settings",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    
    // Chicken Road Management Section
    {
      text: "Chicken Road Management",
      isHeader: true,
      roles: ["owner", "super_admin" , "admin" ],
    },
    {
      text: "Chicken Road Statistics",
      icon: <Assessment />,
      path: "/dashboard/chicken-road/statistics",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Chicken Road History",
      icon: <History />,
      path: "/dashboard/chicken-road/history",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Chicken Road Settings",
      icon: <SettingsIcon />,
      path: "/dashboard/chicken-road/settings",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    
    // 7 Up Down Management Section
    {
      text: "7 Up Down Management",
      isHeader: true,
      roles: ["owner", "super_admin" , "admin" ],
    },
    {
      text: "7 Up Down Statistics",
      icon: <Assessment />,
      path: "/dashboard/seven-up-down/statistics",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "7 Up Down History",
      icon: <History />,
      path: "/dashboard/seven-up-down/history",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "7 Up Down Settings",
      icon: <SettingsIcon />,
      path: "/dashboard/seven-up-down/settings",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    
    // Settings Section
    {
      text: "Settings",
      isHeader: true,
      roles: ["owner", "super_admin" , "admin" ],
    },
    {
      text: "Noticeboard",
      icon: <NotificationsActive />,
      path: "/dashboard/settings/noticeboard",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "How to Play",
      icon: <HelpOutline />,
      path: "/dashboard/settings/how-to-play",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Push Notifications",
      icon: <Send />,
      path: "/dashboard/settings/push-notifications",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Merchant UPI",
      icon: <Payment />,
      path: "/dashboard/settings/merchant-upi",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    {
      text: "Banners",
      icon: <BannerIcon />,
      path: "/dashboard/settings/banners",
      roles: ["owner", "super_admin", "admin"],
      isSubmenu: true,
    },
    {
      text: "Admin Settings",
      icon: <AdminPanelSettings />,
      path: "/dashboard/settings/admin-settings",
      roles: ["owner", "super_admin" , "admin" ],
      isSubmenu: true,
    },
    
    {
      text: "Profile",
      icon: <Person />,
      path: "/dashboard/profile",
      roles: ["owner", "admin", "agent" , "super_admin"],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.user_type)
  );

  const drawer = (
    <div>
      <Toolbar>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1">{user?.username}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.user_type?.toUpperCase()}
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item, index) => {
          if (item.isHeader) {
            return (
              <React.Fragment key={index}>
                <ListItem>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: "bold" }}>
                    {item.text}
                  </Typography>
                </ListItem>
              </React.Fragment>
            );
          }
          
          return (
            <ListItem
              button
              key={item.text}
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{ 
                pl: item.isSubmenu ? 4 : 2,
                borderRadius: item.isSubmenu ? "0 25px 25px 0" : "0",
                mr: item.isSubmenu ? 1 : 0
              }}
              component={motion.div}
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontSize: item.isSubmenu ? "0.875rem" : "1rem",
                  color: item.isSubmenu ? "text.secondary" : "inherit"
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Panel - {user?.user_type?.toUpperCase()}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem
              onClick={() => {
                navigate("/dashboard/profile");
                handleProfileMenuClose();
              }}
            >
              <Person sx={{ mr: 1 }} /> Profile
            </MenuItem>
            <MenuItem onClick={handlePasswordDialogOpen}>
              <Lock sx={{ mr: 1 }} /> Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: "64px",
        }}
      >
        <Routes>
          <Route path="/" element={<ProtectedRoute element={<Overview />} allowedRoles={["owner", "super_admin","admin", "agent"]} />} />
          <Route path="/users" element={<ProtectedRoute element={<UserManagement />} allowedRoles={["owner", "super_admin", "admin", "agent"]} />} />
          <Route path="/create-user" element={<ProtectedRoute element={<CreateUser />} allowedRoles={["owner", "super_admin", "admin", "agent"]} />} />
          <Route path="/wallet" element={<ProtectedRoute element={<WalletManagement />} allowedRoles={["owner", "super_admin", "admin", "agent"]} />} />
          <Route path="/withdrawals" element={<ProtectedRoute element={<WithdrawalManagement />} allowedRoles={["owner"]} />} />
          <Route path="/recharges" element={<ProtectedRoute element={<RechargeManagement />} allowedRoles={["owner"]} />} />
          <Route path="/withdrawal-test" element={<ProtectedRoute element={<WithdrawalTest />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/profile" element={<ProtectedRoute element={<Profile />} allowedRoles={["owner", "admin", "agent", "super_admin"]} />} />
          <Route path="/markets" element={<ProtectedRoute element={<MarketManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/markets/create" element={<ProtectedRoute element={<CreateMarket />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/markets/:id" element={<ProtectedRoute element={<MarketDetails />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/markets/:id/edit" element={<ProtectedRoute element={<CreateMarket />} allowedRoles={["owner", "super_admin", "admin"]} />} />
            {/* Matka Routes */}
          <Route path="/matka/rates" element={<ProtectedRoute element={<MatkaGameRates />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/matka/bets" element={<ProtectedRoute element={<MatkaBets />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/matka/results" element={<ProtectedRoute element={<MatkaDeclareResults />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          {/* Gali Routes */}
          <Route path="/gali/markets" element={<ProtectedRoute element={<GaliMarkets />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/gali/rates" element={<ProtectedRoute element={<GaliGameRates />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/gali/bets" element={<ProtectedRoute element={<GaliBets />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/gali/results" element={<ProtectedRoute element={<GaliDeclareResults />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          
          {/* Aviator Routes */}
          <Route path="/aviator/statistics" element={<ProtectedRoute element={<AviatorStatistics />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/aviator/bets" element={<ProtectedRoute element={<AviatorBetHistory />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/aviator/settings" element={<ProtectedRoute element={<AviatorSettings />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          
          {/* Chicken Road Routes */}
          <Route path="/chicken-road/statistics" element={<ProtectedRoute element={<ChickenRoadStatistics />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/chicken-road/history" element={<ProtectedRoute element={<ChickenRoadHistory />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/chicken-road/settings" element={<ProtectedRoute element={<ChickenRoadSettings />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          
          {/* 7 Up Down Routes */}
          <Route path="/seven-up-down/statistics" element={<ProtectedRoute element={<SevenUpDownStatistics />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/seven-up-down/history" element={<ProtectedRoute element={<SevenUpDownBetHistory />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/seven-up-down/settings" element={<ProtectedRoute element={<SevenUpDownSettings />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          
          {/* Settings Routes */}
          <Route path="/settings/noticeboard" element={<ProtectedRoute element={<NoticeboardManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/settings/how-to-play" element={<ProtectedRoute element={<HowToPlayManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/settings/push-notifications" element={<ProtectedRoute element={<PushNotificationManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/settings/merchant-upi" element={<ProtectedRoute element={<MerchantUPIManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/settings/admin-settings" element={<ProtectedRoute element={<AdminSettingsManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
          <Route path="/settings/banners" element={<ProtectedRoute element={<BannerManagement />} allowedRoles={["owner", "super_admin", "admin"]} />} />
        
        </Routes>
      </Box>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Lock color="primary" />
            <Typography variant="h6">Change Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {passwordError && (
              <Alert severity="error" onClose={() => setPasswordError("")}>
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert severity="success">{passwordSuccess}</Alert>
            )}
            <TextField
              fullWidth
              label="Old Password"
              type="password"
              value={passwordData.old_password}
              onChange={handlePasswordChange("old_password")}
              disabled={passwordLoading}
              autoComplete="current-password"
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordData.new_password}
              onChange={handlePasswordChange("new_password")}
              disabled={passwordLoading}
              autoComplete="new-password"
              helperText="Must be at least 6 characters"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordData.confirm_password}
              onChange={handlePasswordChange("confirm_password")}
              disabled={passwordLoading}
              autoComplete="new-password"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handlePasswordDialogClose}
            disabled={passwordLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            disabled={passwordLoading}
            variant="contained"
            startIcon={passwordLoading ? <CircularProgress size={20} /> : <Lock />}
          >
            {passwordLoading ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
